/**
 * The import preview — `KLQ-20260824-002`, the drawer a paste into an empty selection opens.
 *
 * **It is the mechanism's, not a surface's.** One drawer for every table that will ever declare
 * an `ImportAdapter`, for the reason the spec gives about copy: a preview implemented per table
 * is retro rule 8's inline `WHERE`, and the second one would be subtly more permissive than the
 * first. What it knows about the domain is exactly what the adapter told it — a list of fields
 * with labels, and a verdict per row.
 *
 * **Nothing is written until Import is pressed**, and the button says how many rows it means.
 * That is the same discipline `GenerateDrawers` follows for the tree: preview first, in the
 * same drawer, from the same request shape the write will use. The reason is stronger here
 * than there — a wrong column mapping with a preview is a moment's annoyance, and a wrong
 * column mapping without one silently fills a register with rows whose due dates are somebody's
 * task titles.
 *
 * **The mapping is remembered per surface, and it is only a convenience.** `localStorage` is
 * wrapped so that a private window, a cleared store or a browser that throws on the accessor
 * degrades to auto-matching alone — which is what the first ever paste gets anyway.
 *
 * **A row is excluded, never edited.** The drawer offers a tick per row and nothing else: the
 * place to fix a wrong value is the sheet, where the person already is, and a preview that let
 * you type into it would be a third editor of the same data (`KLQ-20260828-001` owns the grid's
 * one).
 */

import { useEffect, useMemo, useState } from "react";

import { Button } from "../Button";
import { Drawer } from "../Drawer";
import type {
  ImportAdapter,
  ImportMapping,
  ImportOutcome,
  ImportPreview,
} from "@/lib/grid-interop";
import {
  autoMap,
  looksLikeHeader,
  recallMapping,
  rememberMapping,
} from "@/lib/grid-interop";
import { useMessages } from "@/lib/i18n";

import { Select } from "../Select";

import styles from "./TreeTable.module.css";

export interface ImportDrawerProps {
  /** The parsed clipboard, header row included if the sheet had one. */
  readonly grid: readonly (readonly string[])[];
  readonly adapter: ImportAdapter;
  /**
   * What the remembered mapping is filed under. The consumer's own name for this table —
   * `tasks.hierarchy`, `drawings.tree` — because two surfaces with the same columns are still
   * two sheets in the studio's folder.
   */
  readonly surface: string;
  readonly onClose: () => void;
  /** Ran after a write that changed something, so the surface can refetch. */
  readonly onWritten: () => void;
}

/** The mapping control's "this column feeds nothing" option. Never a real field key. */
const IGNORE = "";

/**
 * A verdict, as a word.
 *
 * Spelled out rather than interpolated into a key: the i18n gates read literal keys out of the
 * source, and a key built from a template variable is one they cannot see — which is how a
 * catalog entry goes missing without anything failing. Same table, same reason, as
 * `DrawingsTable`'s `STATUS_KEY`.
 */
const VERDICT_KEY = {
  create: "import.verdict.create",
  update: "import.verdict.update",
  skip: "import.verdict.skip",
  error: "import.verdict.error",
} as const;

export function ImportDrawer({
  grid,
  adapter,
  surface,
  onClose,
  onWritten,
}: ImportDrawerProps) {
  const t = useMessages("app");

  // Whether the sheet's first line names fields. Decided once from the grid the person pasted
  // and then theirs to override — a register whose first task happens to be called "Due" is
  // rare, and a checkbox is cheaper than being clever about it.
  const [hasHeader, setHasHeader] = useState(() =>
    looksLikeHeader(grid[0], adapter.fields),
  );
  const headers = useMemo(
    () => (hasHeader ? (grid[0] ?? []) : []),
    [grid, hasHeader],
  );
  const body = useMemo(
    () => (hasHeader ? grid.slice(1) : [...grid]),
    [grid, hasHeader],
  );
  const width = useMemo(
    () => grid.reduce((most, row) => Math.max(most, row.length), 0),
    [grid],
  );

  const [mapping, setMapping] = useState<ImportMapping>(() => ({}));
  // Auto-match, then let the remembered mapping win where it has an opinion. That order is
  // deliberate: memory is about this person's habit with this sheet, and a field the sheet has
  // since gained a header for should still be found rather than left at the old `null`.
  useEffect(() => {
    const matched = autoMap(headers, adapter.fields);
    const held = recallMapping(surface, headers);
    setMapping(held === null ? matched : { ...matched, ...held });
  }, [headers, adapter.fields, surface]);

  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [checking, setChecking] = useState(false);
  const [excluded, setExcluded] = useState<ReadonlySet<number>>(new Set());
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);

  const missing = adapter.fields.filter(
    (field) => field.required === true && (mapping[field.key] ?? null) === null,
  );

  /**
   * The dry run, re-asked whenever the mapping or the header decision changes.
   *
   * `live` is `usePlanCheck`'s rule in four lines (`features/drawings/planCheck.ts`): a stale
   * answer never renders. A preview describing a mapping the person has already moved on from
   * is worse than an empty one, because it looks like an answer.
   */
  useEffect(() => {
    if (missing.length > 0) {
      setPreview(null);
      return;
    }
    let live = true;
    setChecking(true);
    adapter
      .preview(body, mapping)
      .then((answer) => {
        if (!live) return;
        setPreview(answer);
        setExcluded(new Set());
        setFailure(null);
      })
      .catch((error: unknown) => {
        if (!live) return;
        setPreview(null);
        setFailure(error instanceof Error ? error.message : t("import.failed"));
      })
      .finally(() => {
        if (live) setChecking(false);
      });
    return () => {
      live = false;
    };
    // `missing` is derived from `mapping` on every render, so depending on the array itself
    // would re-run this forever; its length is the only part the effect actually reads.
  }, [adapter, body, mapping, missing.length, t]);

  const writable = (preview?.rows ?? []).filter(
    (row) =>
      (row.verdict === "create" || row.verdict === "update") &&
      !excluded.has(row.index),
  );

  const commit = (): void => {
    setWriting(true);
    setFailure(null);
    adapter
      .commit(
        body,
        mapping,
        writable.map((row) => row.index),
      )
      .then((answer) => {
        setOutcome(answer);
        rememberMapping(surface, headers, mapping);
        if (answer.written > 0) onWritten();
      })
      .catch((error: unknown) => {
        setFailure(error instanceof Error ? error.message : t("import.failed"));
      })
      .finally(() => {
        setWriting(false);
      });
  };

  return (
    <Drawer
      title={t("import.title")}
      closeLabel={t("import.close")}
      onClose={onClose}
      footer={
        <div className={styles.importActions}>
          {outcome === null ? (
            <>
              <Button
                variant="primary"
                loading={writing}
                disabled={writable.length === 0 || checking}
                onClick={commit}
              >
                {t("import.apply", { count: writable.length })}
              </Button>
              <Button variant="secondary" onClick={onClose}>
                {t("import.cancel")}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={onClose}>
              {t("import.done")}
            </Button>
          )}
        </div>
      }
    >
      <div className={styles.importBody}>
        {outcome === null ? null : (
          <p className={styles.importNote}>
            {t("import.written", { count: outcome.written })}
            {outcome.failed.length === 0
              ? ""
              : ` ${t("import.writeFailed", { count: outcome.failed.length })}`}
            {outcome.note === null ? "" : ` ${outcome.note}`}
          </p>
        )}
        {failure === null ? null : (
          <p className={styles.importProblem}>{failure}</p>
        )}

        {/* The header decision comes first because everything below depends on it: get it
            wrong and the mapping control is naming the wrong row's words. */}
        <label className={styles.importHeaderToggle}>
          <input
            type="checkbox"
            checked={hasHeader}
            onChange={(event) => {
              setHasHeader(event.target.checked);
            }}
          />
          {t("import.headerRow")}
        </label>

        <fieldset className={styles.importMapping}>
          <legend>{t("import.mapping")}</legend>
          {adapter.fields.map((field) => (
            <div key={field.key} className={styles.importField}>
              <Select
                label={field.label}
                size="sm"
                value={mapping[field.key] ?? IGNORE}
                onChange={(event) => {
                  const chosen = event.target.value;
                  setMapping((current) => ({
                    ...current,
                    [field.key]: chosen === IGNORE ? null : Number(chosen),
                  }));
                }}
              >
                <option value={IGNORE}>{t("import.ignore")}</option>
                {Array.from({ length: width }, (_unused, index) => (
                  <option key={index} value={index}>
                    {headers[index] !== undefined && headers[index] !== ""
                      ? headers[index]
                      : t("import.column", { number: index + 1 })}
                  </option>
                ))}
              </Select>
            </div>
          ))}
        </fieldset>

        {missing.length > 0 ? (
          <p className={styles.importProblem}>
            {t("import.required", {
              field: missing.map((field) => field.label).join(", "),
            })}
          </p>
        ) : checking && preview === null ? (
          <p className={styles.importNote}>{t("import.checking")}</p>
        ) : preview === null ? null : (
          <>
            {preview.note === null ? null : (
              <p className={styles.importNote}>{preview.note}</p>
            )}
            <table className={styles.importTable}>
              <thead>
                <tr>
                  <th scope="col" />
                  <th scope="col">{t("import.colRow")}</th>
                  <th scope="col">{t("import.colWhat")}</th>
                  <th scope="col">{t("import.colWhere")}</th>
                  <th scope="col">{t("import.colResult")}</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => {
                  const writableRow =
                    row.verdict === "create" || row.verdict === "update";
                  return (
                    <tr
                      key={row.index}
                      className={
                        row.verdict === "error" ? styles.importErrorRow : ""
                      }
                    >
                      <td>
                        {/* Only a row that would be written can be taken out of the write.
                            A tick beside a skipped or refused row would promise that
                            un-ticking it changes something, and it does not. */}
                        {writableRow ? (
                          <input
                            type="checkbox"
                            checked={!excluded.has(row.index)}
                            aria-label={t("import.include", {
                              number: row.index + 1,
                            })}
                            onChange={() => {
                              setExcluded((current) => {
                                const next = new Set(current);
                                if (next.has(row.index)) next.delete(row.index);
                                else next.add(row.index);
                                return next;
                              });
                            }}
                          />
                        ) : null}
                      </td>
                      <td>{row.index + 1}</td>
                      <td>{row.summary}</td>
                      <td>{row.where}</td>
                      <td>
                        {t(VERDICT_KEY[row.verdict])}
                        {row.reason === null ? "" : ` — ${row.reason}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </Drawer>
  );
}
